"use client";

import Link from "next/link";
import { MapPin } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  checkablePostalCode,
  formatMoney,
  scopeName,
  type MarketBand,
} from "@/components/features/price-index/price-check/price-check-model";
import { usePriceCheck, type PriceCheckState } from "@/components/features/price-index/price-check/use-price-check";
import { LANDING_FIELD_BAR_CLASSES } from "./landing-field-bar";
import { LANDING_PRODUCTS, LandingProductPicker, type LandingProductSlug } from "./landing-product-picker";

function bandPosition(band: MarketBand, cents: number): string {
  const span = Math.max(band.maxCents - band.minCents, 1);
  return `${Math.min(100, Math.max(0, ((cents - band.minCents) / span) * 100))}%`;
}

function BandTrack({ band }: { band: MarketBand }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-3 rounded-full bg-muted">
        <div
          className="absolute inset-y-0 rounded-full bg-brand"
          style={{ left: bandPosition(band, band.lowCents), right: `calc(100% - ${bandPosition(band, band.highCents)})` }}
        />
        <span
          aria-hidden
          className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-surface bg-brand-strong shadow"
          style={{ left: bandPosition(band, band.medianCents) }}
        />
      </div>
      <div className="flex justify-between text-xs tabular-nums text-fg-muted">
        <span>{formatMoney(band.minCents, band.currency)}</span>
        <span>
          Most sell for {formatMoney(band.lowCents, band.currency)}–{formatMoney(band.highCents, band.currency)}
        </span>
        <span>{formatMoney(band.maxCents, band.currency)}</span>
      </div>
    </div>
  );
}

function WorthResult({ check }: { check: PriceCheckState }) {
  const { band, isLoading } = check;
  if (!band) {
    return (
      <p className="py-8 text-center text-sm text-fg-muted">
        {isLoading ? "Reading the market…" : "No recent sales here yet. Try another ZIP."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <p className="text-5xl font-semibold tabular-nums tracking-tight">{formatMoney(band.medianCents, band.currency)}</p>
        <p className="pb-1.5 text-sm text-fg-muted">each, typical in {scopeName(band)}</p>
      </div>
      <BandTrack band={band} />
    </div>
  );
}

function WorthActions({ product, postalCode }: { product: LandingProductSlug; postalCode: string | undefined }) {
  const sell = new URLSearchParams({ type: "sell", product });
  const buy = new URLSearchParams({ product });
  if (postalCode) buy.set("zip", postalCode);
  return (
    <div className="flex flex-wrap gap-2">
      <Button asChild variant="primary">
        <a href={`/request-quote?${sell.toString()}`}>List yours</a>
      </Button>
      <Button asChild variant="outline">
        <Link href={`/listing?${buy.toString()}`} prefetch={false}>
          Buy near you
        </Link>
      </Button>
    </div>
  );
}

export function LandingToolWorth() {
  const check = usePriceCheck({ productTypeSlug: LANDING_PRODUCTS[0].slug });
  const product = LANDING_PRODUCTS.find((candidate) => candidate.slug === check.inputs.productTypeSlug)?.slug ?? LANDING_PRODUCTS[0].slug;
  return (
    <div className="flex flex-col gap-5">
      <LandingProductPicker
        value={product}
        onChange={(slug) => check.update({ productTypeSlug: slug, condition: "", sizeKey: "" })}
        className="sm:grid-cols-6"
      />
      <div className={LANDING_FIELD_BAR_CLASSES}>
        <MapPin aria-hidden className="ml-2 size-5 shrink-0 text-fg-muted" />
        <Input
          variant="ghost"
          inputMode="numeric"
          autoComplete="postal-code"
          aria-label="ZIP code"
          placeholder="ZIP code (optional)"
          value={check.inputs.postalCode}
          onChange={(event) => check.update({ postalCode: event.target.value.replace(/\D/g, "").slice(0, 5) })}
          className="flex-1"
        />
      </div>
      <div aria-live="polite">
        <WorthResult check={check} />
      </div>
      <WorthActions product={product} postalCode={checkablePostalCode(check.inputs.postalCode)} />
    </div>
  );
}
